import React, { useEffect, useState } from 'react'
import Styled from 'styled-components'
import Report from '../../common/types/api/report'
import { Recipient } from '../../common/types/form'
import i18n from 'live/services/i18n'
import getReports from '../../api/getReport'
import LiveSpan from './Content/LiveSpan'
import { OptionType } from '../../common/types/utilities'
import SelectRecipientGroup from './RecipientGroups/SelectRecipientGroup'

export interface ManualReportSendData {
    reportSource: Report
    timespan: string
    selectedRecipientGroups: Recipient[]
    dontSendToInactiveGroups?: boolean
}

interface ManualReportSendDataPartial {
    reportSource?: Report
    timespan?: string
    selectedRecipientGroups?: Recipient[]
    dontSendToInactiveGroups?: boolean
}
interface Props {
    manualReportSendData: ManualReportSendData
    onChange: (newData: ManualReportSendData) => void
}

const mapRecipientsToOptionType = (recipients: Recipient[], filterEnabled = false): OptionType[] => {
    return recipients
        ?.map(({ name, enabled }, index) => {
            if (filterEnabled && !enabled) {
                return null
            }

            return {
                label: name,
                value: index
            } as OptionType
        })
        .filter(option => !!option)
}

export default function ManualReportSendPage({ manualReportSendData, onChange }: Props): JSX.Element {
    const [report, setReport] = useState<Report>({} as Report)
    const { reportSource, selectedRecipientGroups = [], timespan } = manualReportSendData
    const recipients = report?.mailSettings?.recipients || []

    useEffect(() => {
        getReports(reportSource.uid)
            .then(res => {
                setReport(res)
            })
            .catch(err => console.error(err))
    }, [reportSource.uid])

    useEffect(() => {
        if (!!recipients && recipients.length > 0) {
            onChange({
                ...manualReportSendData,
                selectedRecipientGroups: recipients
            })
        }
    }, [report])

    return (
        <StyledComponentManualReportSend className="StyledComponentManualReportSend">
            <div className="StyledComponentManualReportSend--body">
                <div className="StyledComponentManualReportSend--body__timespanSelect">
                    <LiveSpan
                        placeholder='Select timespan'
                        span={timespan}
                        setSpan={newSpan => onChange({ ...manualReportSendData, timespan: newSpan })}
                        defaultSpan={null}
                    />
                    <div>
                        <span>{i18n('When its blank, widgets will follow the original span')}</span>
                    </div>
                </div>
                <div className="StyledComponentManualReportSend--body__recipientGroupSelect">
                    <div className="StyledComponentManualReportSend--body__recipientGroupSelect_CustomSelect">
                        <SelectRecipientGroup
                            options={recipients}
                            selected={selectedRecipientGroups
                                .filter(recipient => recipient.enabled)
                                .map(({ uuid }) => uuid)}
                            onChange={newSelectedGroups => {
                                onChange({
                                    ...manualReportSendData,
                                    selectedRecipientGroups: recipients
                                        .filter(({ uuid }) => newSelectedGroups.includes(uuid))
                                        .map(recipient => ({ ...recipient, enabled: true }))
                                })
                            }}
                            editMode={true}
                            required={false}
                            tooltip={i18n('The recipients groups are the ones created for this report')}
                            tooltipMaxWidth={"180px"}
                            placeholder={'Select the Recipient Group'}
                        />
                    </div>
                </div>
            </div>
        </StyledComponentManualReportSend>
    )
}

const StyledComponentManualReportSend = Styled.div`
    .StyledComponentManualReportSend {
        > * {
            margin: 10px;
        }
        &--body {
            > * {
                margin: 15px;
            }
            &__referenceDate {
                &__info {
                    > * {
                        color: ${({ theme }) => theme['gray-300']};
                        margin: 5px 3px;
                    }
                }
            }
            &__timespanSelect {
                width: 95%;
                min-width:275px;
                > span {
                    font: ${({ theme }) => theme['font-light']}
                }
            }
            &__recipientGroupSelect {
                width:95%;
                min-width: 275px;
                &__SendToInactiveCheckbox{
                    margin-top: 10px;
                    &--checkbox {
                        margin: 5px 2px;
                    }
                }
            }
        }
    }
`
